import { Icon } from "../../icon";
import { cn } from "../../utils";
import type { Intent } from "../../tokens";
import { INTENT_CHART_COLOR, type MetricReference } from "./types";

/**
 * MetricChart — the ONE inline-SVG trend curve. Folds the three byte-near
 * renderers that lived in separate files (hero-kpi's `KpiCurve`,
 * dashboard-primary's `PrimaryLine`, the bare `Sparkline`) plus the objectives
 * `Trajectory`. They differed only in which reference line they drew; here that
 * is one prop.
 *
 * It owns the curve CORE only — the `0 0 100 40` adaptive-domain geometry, the
 * area fill, the reference line(s), and the verdict-tinted end-dot. The labels,
 * headers, and axis captions that wrapped the old renderers are COMPOSITION and
 * stay in the call site / density block (MetricRow, MetricPanel) — this is the
 * line that keeps the pattern cohesive without becoming a god-component.
 *
 * Inline SVG only — no chart library on the critical path. `preserveAspectRatio`
 * is "none", so the parent's width/height (via `className`) drives the size; the
 * end-dot is HTML so the stretch can't distort it into an ellipse.
 *
 * @see docs/design/METRIC-PATTERN-PRD.md
 */
export interface MetricChartProps {
  history: number[];
  /** THE distinguishing prop. One or many (array ⇒ goal line + pace ramp). */
  reference?: MetricReference | MetricReference[];
  /**
   * Tints the end-dot via the intent → chart-color map. Deliberately the
   * structural subset `{ intent, label? }`, not the full MetricVerdict union —
   * the chart only needs the tint + an a11y label, so any verdict vocabulary
   * (band, pace, or the dashboard's KpiVerdict) can drive it without being
   * forced into one scale.
   */
  verdict: { intent: Intent; label?: string };
  /** "spark" == the old bare Sparkline (no area / dot / reference). */
  variant?: "spark" | "curve";
  /** "fit" auto-domains over series + references; {min,max} for fixed scale. */
  domain?: "fit" | { min: number; max: number };
  /** Override the end-dot (defaults on for "curve", off for "spark"). */
  showEndDot?: boolean;
  /** Stroke color for the series line. Defaults to the brand chart color. */
  color?: string;
  /** Shown when `history` is empty. */
  emptyLabel?: string;
  /** Parent owns the rendered size (h-8 / h-28 / …). */
  className?: string;
  ariaLabel?: string;
}

const W = 100;
const H = 40;
const PAD_X = 1;
const PAD_Y = 4;

export function MetricChart({
  history,
  reference = { mode: "none" },
  verdict,
  variant = "curve",
  domain = "fit",
  showEndDot,
  color = "var(--chart-1)",
  emptyLabel = "awaiting signal",
  className,
  ariaLabel,
}: MetricChartProps) {
  const refs = Array.isArray(reference) ? reference : [reference];
  const isCurve = variant === "curve";
  const dot = showEndDot ?? isCurve;

  if (!history || history.length === 0) {
    return (
      <div
        className={cn(
          "flex items-center justify-center rounded-md border border-dashed border-border/60 bg-muted/10 type-subtext text-muted-foreground",
          className,
        )}
      >
        <span className="inline-flex items-center gap-1">
          <Icon name="circle-dashed" size="xs" /> {emptyLabel}
        </span>
      </div>
    );
  }

  // A single point is not a line — duplicate it so it renders as a flat baseline
  // spanning the canvas (the KpiCurve/Sparkline single-point rule).
  const pts = history.length === 1 ? [history[0]!, history[0]!] : history;

  // Domain spans the series AND (for curves) every reference point, so the
  // reference is always on-canvas — KpiCurve's adaptive-domain math.
  const refVals: number[] = [];
  if (isCurve) {
    for (const r of refs) {
      if (r.mode === "flat-target") refVals.push(r.value);
      else if (r.mode === "pace-ramp")
        refVals.push(r.start, r.target, r.start + (r.target - r.start) * r.elapsed);
      else if (r.mode === "series") refVals.push(...r.points);
    }
  }
  const all = [...pts, ...refVals];
  const min = domain === "fit" ? Math.min(...all) : domain.min;
  const max = domain === "fit" ? Math.max(...all) : domain.max;
  const span = max - min || 1;
  const stepX = pts.length > 1 ? (W - PAD_X * 2) / (pts.length - 1) : 0;
  const yOf = (v: number) => H - PAD_Y - ((v - min) / span) * (H - PAD_Y * 2);
  const xOf = (i: number) => PAD_X + i * stepX;

  const linePts = pts.map((v, i) => `${xOf(i).toFixed(2)},${yOf(v).toFixed(2)}`).join(" ");
  const lastX = xOf(pts.length - 1);
  const lastY = yOf(pts[pts.length - 1]!);
  const areaPts = `${PAD_X.toFixed(2)},${(H - PAD_Y).toFixed(2)} ${linePts} ${lastX.toFixed(2)},${(H - PAD_Y).toFixed(2)}`;

  return (
    <div className={cn("relative", className)}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="h-full w-full"
        role="img"
        aria-label={ariaLabel ?? `${verdict.label ?? "Metric"} trend`}
      >
        {isCurve && <polygon points={areaPts} fill="var(--chart-1)" fillOpacity={0.08} />}
        {isCurve &&
          refs.map((r, i) => {
            if (r.mode === "flat-target") {
              const ty = yOf(r.value).toFixed(2);
              return (
                <polyline
                  key={i}
                  points={`${PAD_X},${ty} ${(W - PAD_X).toFixed(2)},${ty}`}
                  fill="none"
                  stroke="var(--muted-foreground)"
                  strokeOpacity={0.55}
                  strokeWidth={1}
                  strokeDasharray="2 2"
                  vectorEffect="non-scaling-stroke"
                />
              );
            }
            if (r.mode === "pace-ramp") {
              const expectedNow = r.start + (r.target - r.start) * r.elapsed;
              return (
                <polyline
                  key={i}
                  points={`${PAD_X},${yOf(r.start).toFixed(2)} ${(W - PAD_X).toFixed(2)},${yOf(expectedNow).toFixed(2)}`}
                  fill="none"
                  stroke="var(--primary)"
                  strokeOpacity={0.4}
                  strokeWidth={1}
                  strokeDasharray="3 2"
                  vectorEffect="non-scaling-stroke"
                />
              );
            }
            if (r.mode === "series" && r.points.length === pts.length) {
              return (
                <polyline
                  key={i}
                  points={r.points.map((v, j) => `${xOf(j).toFixed(2)},${yOf(v).toFixed(2)}`).join(" ")}
                  fill="none"
                  stroke="var(--muted-foreground)"
                  strokeOpacity={0.55}
                  strokeWidth={1}
                  strokeDasharray="2 2"
                  vectorEffect="non-scaling-stroke"
                />
              );
            }
            return null;
          })}
        <polyline
          points={linePts}
          fill="none"
          stroke={color}
          strokeWidth={1.5}
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      {dot && (
        <span
          className="absolute h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-card"
          style={{
            left: `${(lastX / W) * 100}%`,
            top: `${(lastY / H) * 100}%`,
            backgroundColor: INTENT_CHART_COLOR[verdict.intent],
          }}
        />
      )}
    </div>
  );
}
